import React, { useEffect, useMemo, useState } from "react";
import PdfViewer from "./PdfViewer";
import { Spinner } from "reactstrap";

function detectType({ mimeType, fileName, fileUrl }) {
  const mt = String(mimeType || "").toLowerCase();
  if (mt) {
    if (mt === "application/pdf" || mt.startsWith("application/pdf")) return "pdf";
    if (
      mt === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      mt === "application/msword" ||
      mt === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      mt === "application/vnd.ms-powerpoint"
    ) return "office";
    if (mt.startsWith("image/")) return "image";
  }
  const str = (fileName || fileUrl || "").toLowerCase();
  if (/\.(pdf)(\?|#|$)/i.test(str)) return "pdf";
  if (/\.(docx?|pptx?)(\?|#|$)/i.test(str)) return "office";
  if (/\.(png|jpe?g|webp|svg)(\?|#|$)/i.test(str)) return "image";
  return "unknown";
}

function officeEmbedUrl(srcUrl) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(srcUrl)}`;
}

export default function DocumentViewer({ fileUrl, mimeType, fileName }) {
  const kind = useMemo(
    () => detectType({ mimeType, fileName, fileUrl }),
    [mimeType, fileName, fileUrl]
  );

  if (!fileUrl) {
    return <div style={{ color: "crimson", padding: 12 }}>No file URL.</div>;
  }

  if (kind === "image") {
    return (
      <div style={{ padding: 12, textAlign: "center", background: "#f7f7f7" }}>
        <img
          src={fileUrl}
          alt={fileName || "Resource image"}
          style={{ maxWidth: "100%", maxHeight: "78vh", objectFit: "contain" }}
        />
      </div>
    );
  }

  if (kind === "office") {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);

    const [showSpinner, setShowSpinner] = useState(true);

    useEffect(() => {
      setLoaded(false);
      setErrored(false);
      setShowSpinner(true);

      const fallback = setTimeout(() => setShowSpinner(false), 10000);
      return () => clearTimeout(fallback);
    }, [fileUrl]);

    const onLoad = () => {
      setLoaded(true);
      setTimeout(() => setShowSpinner(false), 200);
    };

    const onError = () => {
      setErrored(true);
      setShowSpinner(false);
    };

    return (
      <div style={{ position: "relative", width: "100%", height: "80vh" }}>
        <iframe
          key={fileUrl}
          title="Office document"
          src={officeEmbedUrl(fileUrl)}
          style={{ width: "100%", height: "100%", border: 0 }}
          referrerPolicy="no-referrer"
          rel="noopener noreferrer"
          allowFullScreen
          onLoad={onLoad}
          onError={onError}
          loading="lazy"
        />

        {!!showSpinner && !errored && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(1px)",
              zIndex: 2,
            }}
          >
            <div style={{ textAlign: "center", color: "#333" }}>
              <Spinner style={{ width: "3rem", height: "3rem" }} />
              <div className="mt-2">
                {loaded ? "Finalizing…" : "Rendring resource…"}
              </div>
            </div>
          </div>
        )}

        {errored && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.9)",
              zIndex: 3,
              color: "crimson",
              padding: 16,
              textAlign: "center",
            }}
          >
            Failed to load Office preview. Please try again or download the file.
          </div>
        )}
      </div>
    );
  }

  if (kind === "pdf") {
    return <PdfViewer file={fileUrl} />;
  }

  return (
    <div style={{ padding: 12 }}>
      Unsupported file type. Only PDF, Word, and PowerPoint are handled here.
    </div>
  );
}
